import type { ContractorProjectType, OnboardingPrefill } from "@shared/types";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
	MultiImageUpload,
	SingleImageUpload,
} from "@/components/ui/image-upload";
import { StepProgress } from "@/components/ui/step-progress";
import { SuccessScreen } from "@/components/ui/success-screen";
import { createContractorProfile } from "@/features/onboarding/services/onboarding-api.service";
import {
	FieldInput,
	FieldSelect,
	FloatPanel,
	StudioButton,
} from "@/features/studio/theme";
import { useServiceCities, useTrades } from "@/lib/constants/markets";
import {
	fillLocationIfEmpty,
	keepOrFill,
	useApplyPrefillOnce,
} from "../hooks/use-onboarding-prefill";
import { contractorProjectTypeOptions } from "../options";
import { BackLink } from "./back-link";
import {
	BudgetRangeFields,
	budgetErrorKey,
	budgetPayload,
	CityChipsPicker,
	EMPTY_BUDGET,
	EMPTY_LOCATION,
	FieldGroupLabel,
	LocationFields,
	locationErrorKey,
	locationPayload,
	OptionChips,
	parseServiceRadius,
} from "./fields";

type Step = "about" | "work" | "showcase" | "complete";
const STEPS: Step[] = ["about", "work", "showcase", "complete"];
const PANEL_PADDING = "clamp(24px, 6vw, 40px) clamp(20px, 5vw, 32px)";

export interface ContractorOnboardingFlowProps {
	/** Back from the first step. Without it the first step has no Back link. */
	onExit?: () => void;
	/**
	 * Rendered above the first step only, where the Back link to the role
	 * picker goes. The onboarding page passes the role-list retry here; the
	 * way back to another role goes in `backToRole`.
	 */
	exitControl?: React.ReactNode;
	/**
	 * The first step's way back to the role the account already holds,
	 * placed beside Continue: the slot later steps use for their Back.
	 */
	backToRole?: React.ReactNode;
	/** Details the account already gave; fills empty fields once. */
	prefill?: OnboardingPrefill;
	/** The profile was created; the flow shows its success screen next. */
	onProfileCreated: () => void;
	/** The server already holds a finished profile (409). */
	onAlreadyExists: () => void;
	/** The success screen's button. */
	onFinished: () => void;
}

/**
 * Contractor onboarding: about you, your work, then an optional showcase.
 * Nothing is saved until the last step, which can be skipped.
 */
export function ContractorOnboardingFlow({
	onExit,
	exitControl,
	backToRole,
	prefill,
	onProfileCreated,
	onAlreadyExists,
	onFinished,
}: ContractorOnboardingFlowProps) {
	const { t } = useTranslation();
	const cityOptions = useServiceCities().map((city) => city.value);
	const tradeOptions = useTrades().map((trade) => ({
		value: trade.value,
		label: t(trade.labelKey, trade.displayLabel),
	}));
	const [step, setStep] = useState<Step>("about");
	const [isLoading, setIsLoading] = useState(false);
	const [isUploading, setIsUploading] = useState(false);

	// About you
	const [photoUrl, setPhotoUrl] = useState("");
	const [name, setName] = useState("");
	const [businessName, setBusinessName] = useState("");
	const [experienceYears, setExperienceYears] = useState("");
	const [location, setLocation] = useState(EMPTY_LOCATION);

	useApplyPrefillOnce(prefill, (known) => {
		setName((current) => keepOrFill(current, known.name));
		setPhotoUrl((current) => keepOrFill(current, known.profilePictureUrl));
		setLocation((current) => fillLocationIfEmpty(current, known));
	});

	// Your work
	const [trades, setTrades] = useState<string[]>([]);
	const [projectTypes, setProjectTypes] = useState<ContractorProjectType[]>([]);
	const [projectTypesOther, setProjectTypesOther] = useState("");
	const [budget, setBudget] = useState(EMPTY_BUDGET);
	const [serviceAreas, setServiceAreas] = useState<string[]>([]);
	const [radiusKm, setRadiusKm] = useState("");

	// Showcase
	const [workPhotos, setWorkPhotos] = useState<string[]>([]);

	const stepNames: Record<Step, string> = {
		about: t("onboarding.contractor.steps.about"),
		work: t("onboarding.contractor.steps.work"),
		showcase: t("onboarding.contractor.steps.showcase"),
		complete: t("onboarding.contractor.steps.complete"),
	};

	const handleAboutSubmit = (event: React.FormEvent) => {
		event.preventDefault();
		if (!name.trim()) {
			toast.error(t("onboarding.contractor.pleaseEnterYourName"));
			return;
		}
		const locationError = locationErrorKey(location);
		if (locationError) {
			toast.error(t(locationError));
			return;
		}
		setStep("work");
	};

	const handleWorkSubmit = (event: React.FormEvent) => {
		event.preventDefault();
		if (trades.length === 0) {
			toast.error(t("onboarding.contractor.selectAtLeastOneTrade"));
			return;
		}
		if (serviceAreas.length === 0) {
			toast.error(t("onboarding.contractor.selectAtLeastOneArea"));
			return;
		}
		if (Number.isNaN(parseServiceRadius(radiusKm))) {
			toast.error(t("onboarding.contractor.radiusInvalid"));
			return;
		}
		const budgetError = budgetErrorKey(budget);
		if (budgetError) {
			toast.error(t(budgetError));
			return;
		}
		setStep("showcase");
	};

	const submit = async (photos: string[]) => {
		setIsLoading(true);
		try {
			const result = await createContractorProfile({
				name: name.trim(),
				businessName: businessName.trim() || null,
				profilePictureUrl: photoUrl || undefined,
				experienceYears: experienceYears
					? Number.parseInt(experienceYears, 10)
					: undefined,
				...locationPayload(location),
				trades,
				projectTypes,
				projectTypesOther: projectTypes.includes("other")
					? projectTypesOther.trim() || null
					: null,
				...budgetPayload(budget),
				serviceAreas,
				serviceRadiusKm: parseServiceRadius(radiusKm),
				workPhotos: photos,
			});

			if (result.success) {
				onProfileCreated();
				toast.success(t("onboarding.contractor.profileCreated"));
				setStep("complete");
			} else if (result.alreadyExists) {
				toast.info(t("onboarding.contractor.profileAlreadyExists"));
				onAlreadyExists();
			} else {
				toast.error(
					result.error || t("onboarding.contractor.failedToCreateProfile"),
				);
			}
		} catch {
			toast.error(t("common.somethingWentWrong"));
		} finally {
			setIsLoading(false);
		}
	};

	const progress = (
		<StepProgress
			steps={STEPS.map((s) => stepNames[s])}
			currentStep={stepNames[step]}
			className="mb-8"
		/>
	);

	if (step === "complete") {
		return (
			<>
				{progress}
				<SuccessScreen
					title={t("onboarding.contractor.completeTitle")}
					description={t("onboarding.contractor.completeSubtitle")}
					actionLabel={t("common.goToDashboard")}
					onAction={onFinished}
					notice={{
						title: t("onboarding.contractor.verificationPendingTitle"),
						message: t("onboarding.contractor.verificationPendingMsg"),
					}}
				/>
			</>
		);
	}

	return (
		<>
			{step === "about" && exitControl}
			{step === "about" && onExit && <BackLink onClick={onExit} />}
			{progress}

			{step === "about" && (
				<FloatPanel elevation={2} padding={PANEL_PADDING}>
					<h2 className="mb-2 font-display text-2xl font-semibold text-dashboard-text">
						{t("onboarding.contractor.title")}
					</h2>
					<p className="mb-6 font-body text-sm text-dashboard-muted">
						{t("onboarding.contractor.subtitle")}
					</p>

					<form onSubmit={handleAboutSubmit} className="space-y-5">
						<div>
							<FieldGroupLabel>
								{t("onboarding.fields.profilePhoto")}
							</FieldGroupLabel>
							<SingleImageUpload
								label=""
								currentUrl={photoUrl || undefined}
								onUpload={setPhotoUrl}
								onRemove={() => setPhotoUrl("")}
								onUploadingChange={setIsUploading}
							/>
						</div>

						<div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
							<FieldInput
								label={t("onboarding.contractor.yourName")}
								value={name}
								autoComplete="name"
								onChange={(event) => setName(event.target.value)}
								placeholder={t("onboarding.contractor.yourNamePlaceholder")}
								required
							/>
							<FieldInput
								label={t("onboarding.contractor.businessName")}
								value={businessName}
								autoComplete="organization"
								maxLength={200}
								onChange={(event) => setBusinessName(event.target.value)}
								placeholder={t("onboarding.contractor.businessNamePlaceholder")}
							/>
						</div>

						<FieldSelect
							label={t("onboarding.contractor.yearsOfExperience")}
							value={experienceYears || "__none__"}
							onChange={(event) => {
								const value = event.target.value;
								setExperienceYears(value === "__none__" ? "" : value);
							}}
							options={[
								{
									value: "__none__",
									label: t("onboarding.contractor.selectExperience"),
								},
								{ value: "1", label: t("onboarding.contractor.exp1to2") },
								{ value: "3", label: t("onboarding.contractor.exp3to5") },
								{ value: "6", label: t("onboarding.contractor.exp6to10") },
								{ value: "11", label: t("onboarding.contractor.exp10to15") },
								{ value: "16", label: t("onboarding.contractor.exp15plus") },
							]}
						/>

						<LocationFields
							label={t("onboarding.contractor.location")}
							value={location}
							onChange={setLocation}
						/>

						{backToRole ? (
							<div className="flex gap-3" style={{ marginTop: 24 }}>
								{backToRole}
								<StudioButton
									type="submit"
									variant="primary"
									disabled={isUploading}
									style={{ flex: 1 }}
								>
									{t("common.continue")}
								</StudioButton>
							</div>
						) : (
							<StudioButton
								type="submit"
								variant="primary"
								disabled={isUploading}
								style={{ width: "100%", marginTop: 24 }}
							>
								{t("common.continue")}
							</StudioButton>
						)}
					</form>
				</FloatPanel>
			)}

			{step === "work" && (
				<FloatPanel elevation={2} padding={PANEL_PADDING}>
					<h2 className="mb-2 font-display text-2xl font-semibold text-dashboard-text">
						{t("onboarding.contractor.workStepTitle")}
					</h2>
					<p className="mb-6 font-body text-sm text-dashboard-muted">
						{t("onboarding.contractor.workStepSubtitle")}
					</p>

					<form onSubmit={handleWorkSubmit} className="space-y-7">
						<OptionChips
							label={t("onboarding.contractor.specialize")}
							options={tradeOptions}
							selected={trades}
							onChange={setTrades}
						/>

						<OptionChips
							label={t("onboarding.contractor.projectTypes")}
							options={contractorProjectTypeOptions(t)}
							selected={projectTypes}
							onChange={(next) =>
								setProjectTypes(next as ContractorProjectType[])
							}
							otherValue="other"
							otherText={projectTypesOther}
							onOtherTextChange={setProjectTypesOther}
						/>

						<BudgetRangeFields
							label={t("onboarding.contractor.budget")}
							value={budget}
							onChange={setBudget}
						/>

						<div className="space-y-4">
							<CityChipsPicker
								label={t("onboarding.contractor.serviceArea")}
								hint={t("onboarding.contractor.serviceAreaHint")}
								cities={cityOptions}
								selected={serviceAreas}
								onChange={setServiceAreas}
								addPlaceholder={t("onboarding.contractor.addCityPlaceholder")}
							/>
							<FieldInput
								label={t("onboarding.contractor.radius")}
								// No min/max attributes: the range is checked on Continue
								// (handleWorkSubmit), whose toast names 1-500 km.
								type="number"
								inputMode="numeric"
								value={radiusKm}
								onChange={(event) => setRadiusKm(event.target.value)}
								placeholder={t("onboarding.contractor.radiusPlaceholder")}
								style={{ maxWidth: 240 }}
							/>
						</div>

						<div className="flex gap-3 pt-2">
							<StudioButton
								type="button"
								onClick={() => setStep("about")}
								variant="secondary"
								style={{ flex: 1 }}
							>
								{t("common.back")}
							</StudioButton>
							<StudioButton type="submit" variant="primary" style={{ flex: 1 }}>
								{t("common.continue")}
							</StudioButton>
						</div>
					</form>
				</FloatPanel>
			)}

			{step === "showcase" && (
				<FloatPanel elevation={2} padding={PANEL_PADDING}>
					<h2 className="mb-2 font-display text-2xl font-semibold text-dashboard-text">
						{t("onboarding.contractor.showcaseStepTitle")}
					</h2>
					<p className="mb-6 font-body text-sm text-dashboard-muted">
						{t("onboarding.contractor.showcaseStepSubtitle")}
					</p>

					<div className="mb-2">
						<FieldGroupLabel>
							{t("onboarding.contractor.workPhotos")}
						</FieldGroupLabel>
						<MultiImageUpload
							label=""
							currentUrls={workPhotos}
							maxImages={10}
							onUploadingChange={setIsUploading}
							onUpload={(urls) => setWorkPhotos((prev) => [...prev, ...urls])}
							onRemove={(index) =>
								setWorkPhotos((prev) => prev.filter((_, i) => i !== index))
							}
						/>
					</div>
					<p className="font-body text-[13px] text-dashboard-muted">
						{t("onboarding.contractor.showcaseLaterHint")}
					</p>

					<div className="flex flex-col gap-3 pt-6 sm:flex-row">
						<StudioButton
							type="button"
							onClick={() => setStep("work")}
							variant="secondary"
							className="w-full sm:w-auto sm:flex-1"
						>
							{t("common.back")}
						</StudioButton>
						<StudioButton
							type="button"
							variant="secondary"
							disabled={isLoading || isUploading}
							onClick={() => submit([])}
							className="w-full sm:w-auto sm:flex-1"
						>
							{t("common.skipForNow")}
						</StudioButton>
						<StudioButton
							type="button"
							variant="primary"
							disabled={isLoading || isUploading}
							onClick={() => submit(workPhotos)}
							className="w-full sm:w-auto sm:flex-1"
						>
							{isLoading
								? t("common.creating")
								: t("onboarding.contractor.createProfile")}
						</StudioButton>
					</div>
				</FloatPanel>
			)}
		</>
	);
}
